import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import { Container, Section, PageHero, HeroArt, Reveal, Kicker, StatusPill, hudCompactControl } from '../components/ui';
import { CONFERENCE } from '../data/conference';

/* connection motif — an envelope linked into a copper node network */
const ContactArt = (
  <HeroArt viewBox="0 0 340 260" aria-hidden="true">
    {/* node mesh */}
    <line className="frame faint" x1="60" y1="60" x2="170" y2="130" />
    <line className="frame faint" x1="300" y1="56" x2="170" y2="130" />
    <line className="frame faint" x1="58" y1="210" x2="170" y2="130" />
    <line className="frame faint" x1="296" y1="206" x2="170" y2="130" />
    {/* envelope */}
    <rect className="frame" x="118" y="96" width="104" height="72" rx="6" />
    <path className="frame soft" d="M118 102 L170 138 L222 102" />
    {/* outer nodes */}
    <circle className="node hollow" cx="60" cy="60" r="6" />
    <circle className="node hollow" cx="300" cy="56" r="6" />
    <circle className="node hollow" cx="58" cy="210" r="6" />
    <circle className="node hollow" cx="296" cy="206" r="6" />
    <g className="pulse">
      <circle className="node" cx="170" cy="130" r="5" />
    </g>
  </HeroArt>
);

/*
 * The IEMENTech console — a terminal-style assistant. Type a command
 * (or click one) and the conference database answers.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.4fr;
  gap: 1.4rem;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.aside`
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--bg-card);
  padding: clamp(1.8rem, 3.5vw, 2.4rem);

  h2 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1.6rem;
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px dashed var(--line);
  color: var(--ink-dim);
  font-size: 0.94rem;
  line-height: 1.65;

  &:last-of-type {
    border-bottom: none;
  }

  svg {
    color: var(--copper);
    flex: none;
    margin-top: 0.3rem;
    font-size: 0.9rem;
  }

  a {
    color: var(--ink-dim);
    transition: color 0.2s;

    &:hover {
      color: var(--copper-bright);
    }
  }

  .label {
    font-family: var(--font-mono);
    font-size: 0.62rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--ink-faint);
    display: block;
    margin-bottom: 0.25rem;
  }
`;

/* ---------------- terminal ---------------- */

const blinkCaret = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const TerminalShell = styled.div`
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 30px 70px -30px rgba(0, 0, 0, 0.85);
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.3rem;
  border-bottom: 1px solid var(--line);
  background: var(--bg-elev);

  .dots {
    display: flex;
    gap: 7px;

    span {
      width: 11px;
      height: 11px;
      border-radius: 50%;

      &:nth-child(1) {
        background: #e0563c;
      }
      &:nth-child(2) {
        background: var(--copper);
      }
      &:nth-child(3) {
        background: var(--plasma);
      }
    }
  }

  .title {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.14em;
    color: var(--ink-faint);
  }
`;

const Screen = styled.div`
  font-family: var(--font-mono);
  font-size: 0.84rem;
  line-height: 1.75;
  padding: 1.4rem 1.5rem;
  height: 430px;
  overflow-y: auto;
  cursor: text;

  /* faint scanlines for instrument character */
  background-image: repeating-linear-gradient(
    180deg,
    transparent 0px,
    transparent 2px,
    rgba(34, 31, 26, 0.012) 3px
  );

  .sys {
    color: var(--ink-faint);
  }

  .cmd-echo {
    color: var(--ink);

    .ps1 {
      color: var(--plasma);
    }
  }

  .out {
    color: var(--ink-dim);
    white-space: pre-wrap;
    margin-bottom: 0.65rem;

    b {
      color: var(--copper-bright);
      font-weight: 600;
    }
  }
`;

const PromptLine = styled.form`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;

  .ps1 {
    color: var(--plasma);
    white-space: nowrap;
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--copper-bright);
    font-family: var(--font-mono);
    font-size: 0.84rem;
    caret-color: var(--copper-bright);
    min-height: 32px;
  }
`;

const Caret = styled.span`
  display: inline-block;
  width: 8px;
  height: 1em;
  background: var(--copper-bright);
  vertical-align: text-bottom;
  animation: ${blinkCaret} 1.1s steps(1) infinite;
`;

const CommandChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.4rem 1.3rem;
  border-top: 1px solid var(--line);
  background: var(--bg-elev);

  button {
    ${hudCompactControl}
    --compact-cut: 6px;
    font-family: var(--font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    color: var(--ink-dim);
    padding: 0.45rem 0.9rem;
    min-height: 34px;
    transition: all 0.2s;

    &:hover {
      color: var(--copper-bright);
      --compact-rim: linear-gradient(110deg, #d9892c, #403323 48%, #c97120);
    }
  }
`;

const PS1 = 'guest@iementech:~$';

const COMMANDS = {
  help: () =>
    `Available commands:\n  <b>dates</b>      conference & milestone dates\n  <b>fees</b>       registration fee matrix\n  <b>topics</b>     the twelve research tracks\n  <b>venue</b>      where it all happens\n  <b>submit</b>     paper submission guidelines\n  <b>contact</b>    reach the secretariat\n  <b>about</b>      what is IEMENTech?\n  <b>clear</b>      wipe the screen`,
  dates: () =>
    `<b>Conference:</b> 2–4 June 2027 · IIT Guwahati, Assam\n\nMilestones (to be announced):\n  · Full paper submission deadline\n  · Acceptance notification\n  · Camera-ready submission\n  · Registration deadline`,
  fees: () =>
    `Registration fees (incl. 18% GST for Indian categories):\n\n  <b>Students</b>     early ₹9,500  · late ₹10,000 · IEEE ₹9,000\n  <b>Researchers</b>  early ₹10,500 · late ₹11,000 · IEEE ₹10,000\n  <b>Industry</b>     early ₹12,500 · late ₹13,000 · IEEE ₹12,000\n  <b>Foreign</b>      early $300    · late $350    · IEEE $250`,
  topics: () =>
    `Twelve tracks:\n  Semiconductor Device Physics · Nano-Materials & Opto-Electronics\n  Industrial Electronics & Control · VLSI & Embedded Systems\n  Micro-Electronic Device Fabrication · Renewable Energy\n  Wireless Communication & Computing · Signal & Image Processing\n  AI, ML & IoT · MEMS & Sensors · Microwave & Antenna\n  Robotics & Mechatronics\n\nRun <b>submit</b> to learn how to contribute.`,
  venue: () =>
    `<b>Indian Institute of Technology Guwahati</b>\nNorth Guwahati, Guwahati, Assam, India\n\nThree days on the banks of the Brahmaputra.\nWeb: ${CONFERENCE.website}`,
  submit: () =>
    `Submission guidelines:\n  · Original unpublished papers only\n  · Max <b>6 pages</b> incl. figures & tables\n  · <b>IEEE format</b> (ieee.org/conferences/publishing/templates)\n  · Via CMT — portal opening soon\n  · Double-blind review by 2+ experts\n\nQueries: ${CONFERENCE.altEmail}`,
  contact: () =>
    `Organizing secretariat:\n  <b>Email:</b> ${CONFERENCE.email}\n         ${CONFERENCE.altEmail}\n  <b>Phone:</b> ${CONFERENCE.landline}\n         ${CONFERENCE.phones[0]} · ${CONFERENCE.phones[1]}\n  <b>Web:</b>   ${CONFERENCE.website}`,
  about: () =>
    `<b>IEMENTech 2027</b> — ${CONFERENCE.fullName}.\n\nJointly organized by Dept. of ECE, IEM Kolkata and IIT Guwahati,\nwith technical co-sponsorship of IEEE Kolkata Section.\nThe 10th edition. Three days. One frontier.`,
  register: () =>
    `Registration portal opens soon. Run <b>fees</b> for the fee matrix,\nor visit the Registration page for categories & benefits.`,
};

const BOOT_LINES = [
  'IEMENTech OS v10.0 — conference information system',
  '© 2027 IEM Kolkata × IIT Guwahati · IEEE Kolkata Section',
  "Type 'help' to list commands.",
];

export default function Contact() {
  const [history, setHistory] = useState(
    BOOT_LINES.map((line) => ({ type: 'sys', text: line }))
  );
  const [value, setValue] = useState('');
  const [cmdStack, setCmdStack] = useState([]);
  const [stackPos, setStackPos] = useState(-1);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [history]);

  const run = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;

    if (cmd === 'clear') {
      setHistory([{ type: 'sys', text: "Screen cleared. Type 'help' to list commands." }]);
      setValue('');
      return;
    }

    const handler = COMMANDS[cmd];
    const output = handler
      ? handler()
      : `command not found: ${cmd}\nDid you mean <b>help</b>, <b>dates</b>, <b>fees</b>, <b>venue</b> or <b>contact</b>?`;

    setHistory((h) => [...h, { type: 'cmd', text: raw.trim() }, { type: 'out', text: output }]);
    setCmdStack((s) => [raw.trim(), ...s]);
    setStackPos(-1);
    setValue('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(stackPos + 1, cmdStack.length - 1);
      if (cmdStack[next]) {
        setStackPos(next);
        setValue(cmdStack[next]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = stackPos - 1;
      setStackPos(next);
      setValue(next >= 0 ? cmdStack[next] : '');
    }
  };

  return (
    <>
      <PageHero
        kicker="Contact Us"
        index="08"
        title={
          <>
            Open a <em style={{ fontStyle: 'normal', color: 'var(--copper-bright)' }}>channel.</em>
          </>
        }
        lead="Query the conference console for instant answers, or reach the organizing team directly."
        art={ContactArt}
      />

      <Section>
        <Container>
          <Grid>
            <Reveal>
              <InfoPanel>
                <Kicker>Direct Lines</Kicker>
                <h2>Organizing Secretariat</h2>
                <InfoRow>
                  <FaEnvelope aria-hidden="true" />
                  <div>
                    <span className="label">Email</span>
                    <a href={`mailto:${CONFERENCE.email}`}>{CONFERENCE.email}</a>
                    <br />
                    <a href={`mailto:${CONFERENCE.altEmail}`}>{CONFERENCE.altEmail}</a>
                  </div>
                </InfoRow>
                <InfoRow>
                  <FaPhoneAlt aria-hidden="true" />
                  <div>
                    <span className="label">Phone</span>
                    {CONFERENCE.landline}
                    <br />
                    {CONFERENCE.phones[0]}
                    <br />
                    {CONFERENCE.phones[1]}
                  </div>
                </InfoRow>
                <InfoRow>
                  <FaMapMarkerAlt aria-hidden="true" />
                  <div>
                    <span className="label">Venue</span>
                    {CONFERENCE.address}
                  </div>
                </InfoRow>
                <InfoRow>
                  <FaGlobe aria-hidden="true" />
                  <div>
                    <span className="label">Website</span>
                    <a href={CONFERENCE.website} target="_blank" rel="noopener noreferrer">
                      iementech.iem.edu.in
                    </a>
                  </div>
                </InfoRow>
              </InfoPanel>
            </Reveal>

            <Reveal delay={100}>
              <TerminalShell>
                <TitleBar>
                  <div className="dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="title">iementech — assistant — 80×24</span>
                  <StatusPill>Online</StatusPill>
                </TitleBar>

                <Screen onClick={() => inputRef.current && inputRef.current.focus()} aria-live="polite">
                  {history.map((entry, i) => {
                    if (entry.type === 'sys') {
                      return (
                        <p className="sys" key={i}>
                          {entry.text}
                        </p>
                      );
                    }
                    if (entry.type === 'cmd') {
                      return (
                        <p className="cmd-echo" key={i}>
                          <span className="ps1">{PS1}</span> {entry.text}
                        </p>
                      );
                    }
                    return (
                      <p
                        className="out"
                        key={i}
                        // command output is static app-defined strings with <b> markers only
                        dangerouslySetInnerHTML={{ __html: entry.text }}
                      />
                    );
                  })}

                  <PromptLine
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(value);
                    }}
                  >
                    <span className="ps1">{PS1}</span>
                    <input
                      ref={inputRef}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={onKeyDown}
                      aria-label="Terminal command input"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      maxLength={60}
                    />
                    {!value && <Caret aria-hidden="true" />}
                  </PromptLine>
                  <div ref={endRef} />
                </Screen>

                <CommandChips aria-label="Quick commands">
                  {['help', 'dates', 'fees', 'topics', 'venue', 'submit', 'contact', 'clear'].map((c) => (
                    <button key={c} type="button" onClick={() => run(c)}>
                      {c}
                    </button>
                  ))}
                </CommandChips>
              </TerminalShell>
            </Reveal>
          </Grid>
        </Container>
      </Section>
    </>
  );
}
